/**
 * "Open in SharePoint" / "Open in Google Drive" for the open workspace file:
 * one icon button per store the organization has connected. A store that
 * cannot take a copy right now (uploads not allowed, no location set) still
 * shows, disabled, with the reason in its tooltip — a missing button is a
 * mystery, a disabled one is an instruction.
 *
 * The click copies the file into the store and opens the copy in a new tab;
 * the tab is opened before the round-trip so popup blockers stay quiet.
 */
import { LoaderCircle } from "lucide-react";
import { useCallback, useState } from "react";

import { MenuItem } from "~/components/ui/menu";
import { Button } from "~/components/ui/button";
import { stackedThreadToast, toastManager } from "~/components/ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { resolveConnectorIconFallback } from "~/lib/connectorIcons";
import {
  openInDocumentStore,
  useDocumentStores,
  type DocumentStoreAvailability,
  type DocumentStoreId,
} from "~/state/documentStores";

export function OpenInDocumentStoreButtons(props: {
  readonly threadId: string;
  /** The file's absolute path in the workspace, as the panel holds it. */
  readonly path: string;
  readonly fileName: string;
  /** Writes the open editor's pending edits before the copy is taken. */
  readonly beforeExport?: (() => Promise<void>) | undefined;
}) {
  const stores = useDocumentStores(props.threadId);
  if (stores.length === 0) return null;
  return (
    <>
      {stores.map((store) => (
        <OpenInDocumentStoreButton
          key={store.id}
          store={store}
          threadId={props.threadId}
          path={props.path}
          fileName={props.fileName}
          beforeExport={props.beforeExport}
        />
      ))}
    </>
  );
}

export function OpenInDocumentStoreButton(props: {
  readonly store: DocumentStoreAvailability;
  readonly threadId: string;
  readonly path: string;
  readonly fileName: string;
  readonly beforeExport?: (() => Promise<void>) | undefined;
  readonly open?: typeof openInDocumentStore;
}) {
  const { store } = props;
  const { busy, label, iconUrl, handleClick } = useDocumentStoreOpen(props);

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={label}
            aria-disabled={!store.available || undefined}
            data-store={store.id}
            size="icon-xs"
            variant="outline"
            className={store.available ? undefined : "opacity-50"}
            onClick={handleClick}
          >
            {busy ? (
              <LoaderCircle aria-hidden="true" className="size-3.5 animate-spin" />
            ) : iconUrl ? (
              <img aria-hidden="true" alt="" src={iconUrl} className="size-3.5" />
            ) : (
              <span aria-hidden="true" className="text-[10px] font-semibold">
                {store.label.slice(0, 1)}
              </span>
            )}
          </Button>
        }
      />
      <TooltipPopup>{store.available ? label : (store.reason ?? label)}</TooltipPopup>
    </Tooltip>
  );
}

/** Keep the async export and error handling identical in cards and the panel. */
function useDocumentStoreOpen(props: Parameters<typeof OpenInDocumentStoreButton>[0]) {
  const { store, threadId, path, fileName, beforeExport } = props;
  const open = props.open ?? openInDocumentStore;
  const [busy, setBusy] = useState(false);
  const label = `Open in ${store.label}`;
  const iconUrl = resolveConnectorIconFallback({ id: store.id });

  const handleClick = useCallback(() => {
    if (busy || !store.available) return;
    setBusy(true);
    void open({ threadId, path, store: store.id as DocumentStoreId, beforeExport })
      .catch((error: unknown) => {
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: `Unable to open ${fileName} in ${store.label}`,
            description: error instanceof Error ? error.message : "An error occurred.",
          }),
        );
      })
      .finally(() => setBusy(false));
  }, [beforeExport, busy, fileName, open, path, store, threadId]);

  return { busy, label, iconUrl, handleClick };
}

export function OpenInDocumentStoreMenuItem(
  props: Parameters<typeof OpenInDocumentStoreButton>[0],
) {
  const { store } = props;
  const { busy, iconUrl, handleClick } = useDocumentStoreOpen(props);
  return (
    <MenuItem
      disabled={!store.available || busy}
      onClick={handleClick}
      title={store.reason ?? undefined}
    >
      {busy ? (
        <LoaderCircle className="size-3.5 animate-spin" />
      ) : iconUrl ? (
        <img alt="" src={iconUrl} className="size-5 shrink-0 object-contain" />
      ) : null}
      <span className="flex-1 leading-5">{store.label}</span>
    </MenuItem>
  );
}
